/**
 * PlatformApplyPage — Public form for schools/institutions to apply to join the platform.
 * Hits POST /api/public/superadmin/applications
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Building2, CheckCircle2, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  school_name: '',
  institution_type: 'school',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  country: '',
  city: '',
  address: '',
  estimated_students: '',
  estimated_staff: '',
  preferred_plan: 'basic',
  website: '',
  message: '',
};

const PlatformApplyPage = () => {
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan');
  const [form, setForm] = useState({ ...INITIAL_FORM, preferred_plan: preselectedPlan || INITIAL_FORM.preferred_plan });
  const [submitted, setSubmitted] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    // Plans endpoint is admin-only; show hardcoded list on public form.
    setPlans([
      { code: 'trial', name: 'Trial — Free 14 days' },
      { code: 'basic', name: 'Basic — $49/mo' },
      { code: 'standard', name: 'Standard — $149/mo' },
      { code: 'premium', name: 'Premium — $349/mo' },
      { code: 'enterprise', name: 'Enterprise — Custom' },
    ]);
  }, []);

  const submitMutation = useMutation({
    mutationFn: async (data) => {
      const body = {
        ...data,
        estimated_students: data.estimated_students ? parseInt(data.estimated_students) : null,
        estimated_staff: data.estimated_staff ? parseInt(data.estimated_staff) : null,
      };
      const res = await axios.post(`${API_URL}/api/public/superadmin/applications`, body);
      return res.data;
    },
    onSuccess: (data) => {
      setSubmitted(data);
      toast.success('Application submitted!');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Submission failed'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.school_name || !form.contact_person || !form.contact_email) {
      toast.error('Please fill all required fields');
      return;
    }
    submitMutation.mutate(form);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 flex items-center justify-center p-4" data-testid="platform-apply-success">
        <Card className="max-w-md w-full bg-white/5 backdrop-blur-xl border-white/10 text-white">
          <CardContent className="pt-10 pb-8 space-y-4 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold">Application Received</h2>
            <p className="text-slate-300 text-sm">
              Thanks for applying! Our platform team will review your application and get back to you within 48 hours.
            </p>
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <p className="text-xs text-slate-400 uppercase tracking-wider">Application ID</p>
              <p className="text-sm font-mono font-bold text-indigo-300 mt-1" data-testid="application-id">
                {submitted.application_id}
              </p>
            </div>
            <Link to="/">
              <Button variant="outline" className="text-white border-white/20 bg-white/5 hover:bg-white/10">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900" data-testid="platform-apply-page">
      <header className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center">
            <Building2 className="h-7 w-7 text-indigo-300" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Apply to Join the Platform</h1>
            <p className="text-slate-400 text-sm mt-1 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Onboard your school in minutes — 14-day free trial included
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pb-16">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
            <CardHeader>
              <CardTitle className="text-base">Institution Information</CardTitle>
              <CardDescription className="text-slate-400">Tell us about your school</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">School / Institution Name *</Label>
                  <Input value={form.school_name} onChange={e => setForm({ ...form, school_name: e.target.value })}
                         required data-testid="apply-school-name" className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Institution Type</Label>
                  <Select value={form.institution_type} onValueChange={v => setForm({ ...form, institution_type: v })}>
                    <SelectTrigger data-testid="apply-institution-type" className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="school">School (K-12)</SelectItem>
                      <SelectItem value="college">College</SelectItem>
                      <SelectItem value="university">University</SelectItem>
                      <SelectItem value="bible_college">Bible College / Seminary</SelectItem>
                      <SelectItem value="training_center">Training Center</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Country</Label>
                  <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}
                         data-testid="apply-country" className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">City</Label>
                  <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                         data-testid="apply-city" className="bg-white/5 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Address</Label>
                <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                       className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">Website</Label>
                <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                       placeholder="https://..." className="bg-white/5 border-white/10 text-white" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
            <CardHeader>
              <CardTitle className="text-base">Primary Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Contact Person *</Label>
                  <Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })}
                         required data-testid="apply-contact-person" className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Phone</Label>
                  <Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                         data-testid="apply-contact-phone" className="bg-white/5 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                       required data-testid="apply-contact-email" className="bg-white/5 border-white/10 text-white" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
            <CardHeader>
              <CardTitle className="text-base">Plan & Scale</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Estimated Students</Label>
                  <Input type="number" value={form.estimated_students}
                         onChange={e => setForm({ ...form, estimated_students: e.target.value })}
                         data-testid="apply-est-students" className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Estimated Staff</Label>
                  <Input type="number" value={form.estimated_staff}
                         onChange={e => setForm({ ...form, estimated_staff: e.target.value })}
                         data-testid="apply-est-staff" className="bg-white/5 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Preferred Plan</Label>
                <Select value={form.preferred_plan} onValueChange={v => setForm({ ...form, preferred_plan: v })}>
                  <SelectTrigger data-testid="apply-preferred-plan" className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Anything we should know?</Label>
                <textarea
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-white min-h-[90px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your current setup, pain points, or integrations you need..."
                  data-testid="apply-message"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" size="lg" disabled={submitMutation.isPending}
                    className="bg-indigo-500 hover:bg-indigo-400 text-white px-10"
                    data-testid="submit-platform-application-btn">
              {submitMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Submit Application
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default PlatformApplyPage;
